import { useEffect, useRef, useState, useCallback } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { generateThumbnail, getVideoDuration } from "@/lib/videoThumbnail";
import ContextInputPanel from "./ContextInputPanel";
import CaptureHeader from "./CaptureHeader";
import LockedFeatureSheet from "@/components/LockedFeatureSheet";
import { useEntitlements } from "@/hooks/useEntitlements";

export interface ShortieCaptureData {
  videoBlob: Blob;
  thumbnailBlob: Blob;
  durationSeconds: number;
  mimeType: string;
  contextBlob?: Blob;
  contextText?: string;
  approvalStatus?: 'pending' | 'private';
}

interface Props {
  onSave: (data: ShortieCaptureData) => void;
  onFinishLater?: (data: ShortieCaptureData) => void;
  onCancel: () => void;
}

type Step = "ready" | "recording" | "preview";

// 3s grace past the tier cap tolerates codec flushes; warning banner fires
// when ≤ 10s remain on the clock.
const GRACE_SECONDS = 3;
const WARNING_TAIL_SECONDS = 10;
// Fallback when entitlements are still loading — matches Seedling/Keeper cap.
const DEFAULT_MAX_SECONDS = 30;

function getSupportedMimeType(): string {
  const types = [
    "video/mp4;codecs=h264,aac",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s) % 60).padStart(2, "0")}`;
}

export default function ShortieCapture({ onSave, onFinishLater, onCancel }: Props) {
  const { t } = useTranslation();
  const { maxVideoDurationSeconds, videoRemainingThisWeek, isLoading: entitlementsLoading } = useEntitlements();
  // Effective cap for this session. `null` = unlimited tier → use DEFAULT as
  // a hard safety ceiling so runaway recordings can't happen. In practice the
  // matrix has no unlimited tier yet (30/30/60), so this is belt + braces.
  const maxSeconds = maxVideoDurationSeconds ?? DEFAULT_MAX_SECONDS;
  const warningAt = Math.max(0, maxSeconds - WARNING_TAIL_SECONDS);
  const weeklyExhausted = !entitlementsLoading && videoRemainingThisWeek === 0;
  const [step, setStep] = useState<Step>("ready");
  const [elapsed, setElapsed] = useState(0);
  const [warningShown, setWarningShown] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);
  const [mimeType, setMimeType] = useState("");
  const [streamReady, setStreamReady] = useState(false);
  const [capPaywallOpen, setCapPaywallOpen] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const startTimeRef = useRef(0);
  // When true, recorder.onstop discards chunks and returns to ready instead
  // of building a preview. Used to cancel a recording mid-flight.
  const cancelledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
      clearInterval(timerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Request camera on mount
  useEffect(() => {
    if (step !== "ready") return;
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setStreamReady(true);
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream;
        }
      } catch {
        if (!cancelled) setPermissionDenied(true);
      }
    })();

    return () => { cancelled = true; };
  }, [step]);

  const startRecording = () => {
    if (!streamRef.current) return;

    const mime = getSupportedMimeType();
    setMimeType(mime);
    chunksRef.current = [];

    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: mime || undefined,
      videoBitsPerSecond: 2_000_000,
      audioBitsPerSecond: 128_000,
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      clearInterval(timerRef.current);

      // Cancelled mid-recording — discard chunks and go back to ready.
      // The ready-step useEffect will re-acquire the camera stream.
      if (cancelledRef.current) {
        cancelledRef.current = false;
        chunksRef.current = [];
        stopStream();
        setElapsed(0);
        setWarningShown(false);
        setStreamReady(false);
        setStep("ready");
        return;
      }

      const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });

      // Compute elapsed from the ref, not the `elapsed` state — onstop's
      // closure captures the state value from when startRecording ran (0),
      // and never sees the timer's setElapsed updates.
      const measuredElapsed = Math.max(
        0,
        (Date.now() - startTimeRef.current) / 1000,
      );

      try {
        const [thumb, dur] = await Promise.all([
          generateThumbnail(blob),
          getVideoDuration(blob),
        ]);
        setVideoBlob(blob);
        setThumbnailBlob(thumb);
        // MediaRecorder WebM blobs often report Infinity for duration because
        // the container isn't finalised with a Duration element. Fall back to
        // the wall-clock elapsed we tracked during recording.
        setDuration(
          Number.isFinite(dur) && dur > 0 ? dur : measuredElapsed,
        );
        setPreviewUrl(URL.createObjectURL(blob));
        setStep("preview");
      } catch {
        // Fallback if thumbnail fails — still allow save
        setVideoBlob(blob);
        setDuration(measuredElapsed);
        setPreviewUrl(URL.createObjectURL(blob));
        setStep("preview");
      }

      stopStream();
    };

    recorderRef.current = recorder;
    recorder.start(1000); // collect in 1s chunks
    startTimeRef.current = Date.now();
    setElapsed(0);
    setWarningShown(false);
    setStep("recording");
    // Re-attach stream after React re-renders the recording-step video element
    requestAnimationFrame(() => {
      if (liveVideoRef.current && streamRef.current) {
        liveVideoRef.current.srcObject = streamRef.current;
      }
    });

    timerRef.current = setInterval(() => {
      const secs = (Date.now() - startTimeRef.current) / 1000;
      setElapsed(secs);

      if (secs >= warningAt && !warningShown) {
        setWarningShown(true);
      }

      if (secs >= maxSeconds + GRACE_SECONDS) {
        recorder.stop();
      }
    }, 100);
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
  };

  const cancelRecording = () => {
    cancelledRef.current = true;
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    } else {
      // Recorder already stopped — reset state manually.
      clearInterval(timerRef.current);
      cancelledRef.current = false;
      chunksRef.current = [];
      stopStream();
      setElapsed(0);
      setWarningShown(false);
      setStreamReady(false);
      setStep("ready");
    }
  };

  const handleRetry = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setVideoBlob(null);
    setThumbnailBlob(null);
    setPreviewUrl(null);
    setElapsed(0);
    setWarningShown(false);
    setStreamReady(false);
    setStep("ready");
  };

  const handleSave = (contextBlob?: Blob, contextText?: string, approvalStatus?: 'pending' | 'private') => {
    if (!videoBlob) return;
    onSave({
      videoBlob,
      thumbnailBlob: thumbnailBlob ?? videoBlob, // fallback
      durationSeconds: duration,
      mimeType: mimeType || "video/webm",
      contextBlob,
      contextText,
      approvalStatus,
    });
  };

  // Progress bar color
  const progressColor =
    elapsed >= maxSeconds - 3
      ? "#E24B4A"
      : elapsed >= warningAt
        ? "#F2A623"
        : "#D85A30";

  const progressPercent = Math.min((elapsed / maxSeconds) * 100, 100);
  const countdown = Math.max(0, Math.ceil(maxSeconds - elapsed));

  // ── Permission denied ─────────────────────────────────────────────────────
  if (permissionDenied) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <p className="text-base font-medium text-foreground">
          Camera access is needed to record a Shortie
        </p>
        <p className="text-sm text-muted-foreground">
          Allow camera and microphone access in your browser settings, then try again.
        </p>
        <Button variant="outline" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }

  // ── Preview step ──────────────────────────────────────────────────────────
  if (step === "preview" && previewUrl) {
    return (
      <div className="flex flex-col items-center gap-4 w-full pt-8">
        <CaptureHeader
          title={t("capture.title.shortie")}
          onBack={handleRetry}
          backLabel={t("capture.preview.retake")}
        />
        <div className="relative w-full max-w-sm">
          <video
            ref={previewVideoRef}
            src={previewUrl}
            controls
            playsInline
            autoPlay
            loop
            className="w-full rounded-xl"
          />
          <span className="absolute top-2 right-2 bg-black/60 text-white text-xs font-mono px-2 py-0.5 rounded">
            {formatTime(duration)}
          </span>
        </div>

        <ContextInputPanel
          onProcess={(ctx) => handleSave(ctx.contextBlob, ctx.contextText, ctx.approvalStatus)}
          onSaveLater={onFinishLater ? () => {
            if (!videoBlob) return;
            onFinishLater({ videoBlob, thumbnailBlob: thumbnailBlob ?? videoBlob, durationSeconds: duration, mimeType: mimeType || "video/webm" });
          } : undefined}
        />
      </div>
    );
  }

  // ── Recording step ────────────────────────────────────────────────────────
  if (step === "recording") {
    return (
      <div className="flex flex-col items-center gap-3 w-full">
        <div className="relative w-full max-w-sm">
          {/* Live camera preview */}
          <video
            ref={liveVideoRef}
            autoPlay
            muted
            playsInline
            className="w-full rounded-xl"
            style={{ transform: "scaleX(1)" }}
          />

          {/* Cancel — discards the in-flight recording and returns to ready. */}
          <button
            type="button"
            onClick={cancelRecording}
            aria-label={t("capture.shortie.cancelRecording")}
            className="absolute top-3 left-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Recording indicator */}
          <div className="absolute top-3 left-14 flex items-center gap-1.5 bg-black/60 rounded-full px-2.5 py-1">
            <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-white text-xs font-medium">Recording</span>
          </div>

          {/* Timer */}
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-mono px-2.5 py-1 rounded-full">
            {formatTime(elapsed)}
          </div>

          {/* Countdown overlay (last 10 seconds) */}
          {elapsed >= warningAt && countdown <= WARNING_TAIL_SECONDS && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span
                className="text-white font-bold"
                style={{
                  fontSize: 72,
                  textShadow: "0 2px 20px rgba(0,0,0,0.6)",
                }}
              >
                {countdown}
              </span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-sm">
          <div
            className="w-full overflow-hidden"
            style={{ height: 4, borderRadius: 2, backgroundColor: "#E5E7EB" }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 2,
                width: `${progressPercent}%`,
                backgroundColor: progressColor,
                transition: "width 0.1s linear, background-color 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Warning banner */}
        {warningShown && countdown > 0 && (
          <div className="w-full max-w-sm rounded-lg px-3 py-2 text-center text-xs font-medium"
               style={{ backgroundColor: "rgba(196,91,75,0.12)", color: "#8B3A2E" }}>
            Wrapping up in {countdown} seconds... 🔥
          </div>
        )}

        <Button onClick={stopRecording} className="px-8 mt-1">
          Done
        </Button>
      </div>
    );
  }

  // ── Weekly cap paywall — blocks the Ready step entirely for Seedling. ──
  if (weeklyExhausted) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <span style={{ fontSize: 32, lineHeight: 1 }}>🎬</span>
        <div className="space-y-1">
          <p className="text-base font-medium text-foreground">
            {t("capture.limits.videoWeeklyExhaustedTitle")}
          </p>
          <p className="text-sm text-muted-foreground max-w-[260px]">
            {t("capture.limits.videoWeeklyExhaustedBody")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel} className="px-6">
            {t("common.close")}
          </Button>
          <Button onClick={() => setCapPaywallOpen(true)} className="px-6">
            {t("capture.limits.upgrade")}
          </Button>
        </div>
        <LockedFeatureSheet
          feature="video_cap"
          open={capPaywallOpen}
          onClose={() => setCapPaywallOpen(false)}
        />
      </div>
    );
  }

  // ── Ready step ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <div className="relative w-full max-w-sm">
        {/* Cancel button */}
        <button
          onClick={onCancel}
          className="absolute top-3 left-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Live camera preview */}
        <video
          ref={liveVideoRef}
          autoPlay
          muted
          playsInline
          className="w-full rounded-xl bg-gray-900"
          style={{ minHeight: 300 }}
        />
      </div>

      <p className="text-xs text-muted-foreground">{maxSeconds} seconds · rear camera</p>

      {/* Record button */}
      <button
        onClick={startRecording}
        disabled={!streamReady}
        className="flex h-[72px] w-[72px] items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40"
        style={{
          background: "linear-gradient(135deg, #E24B4A, #D85A30)",
          boxShadow: "0 4px 24px -4px rgba(226,75,74,0.4)",
        }}
      >
        <div className="h-6 w-6 rounded-sm bg-white" />
      </button>
    </div>
  );
}
